import {
  ADVANCED_ORDER_PROVIDER_OVERRIDES,
  getAdvancedOrderProvider,
  isAdvancedOrderTypeSupported,
  isLimitOrderSupportedChainId,
  type AdvancedOrderProviderOverrides,
  type AdvancedOrderType,
} from '@/constants/advancedOrder';

import {
  type CommonValidationInput,
  type CommonValidationResult,
  appendHighPriceImpactResult,
  appendRequiredTokenResults,
  appendTokenRestrictionResults,
} from './common';

/** Code-discriminated validation outcomes only the Advanced-order validator emits. */
export type AdvancedValidationResult =
  | { readonly code: 'sell-amount-required'; readonly severity: 'blocking' }
  | {
      readonly code: 'advanced-order-type-unsupported';
      readonly severity: 'blocking';
      readonly chainId: number;
      readonly orderType: AdvancedOrderType;
    }
  | { readonly code: 'smart-wallet-unsupported'; readonly severity: 'blocking' }
  | { readonly code: 'wrap-unwrap-unsupported'; readonly severity: 'blocking' }
  | { readonly code: 'native-token-unsupported'; readonly severity: 'blocking' }
  | { readonly code: 'superstate-token-unsupported'; readonly severity: 'blocking' }
  | {
      readonly code: 'token-decimals-unavailable';
      readonly severity: 'blocking';
      readonly side: 'sell' | 'buy';
    }
  | { readonly code: 'token-needs-approval-increase'; readonly severity: 'blocking' }
  | { readonly code: 'limit-rate-invalid'; readonly severity: 'blocking' };

/** Ordered results an Advanced-order validation can produce. */
export type AdvancedValidationResults = Array<CommonValidationResult | AdvancedValidationResult>;

/** Normalized facts needed to validate a same-chain Advanced order. */
export type AdvancedOrderValidationInput = CommonValidationInput & {
  readonly orderType: AdvancedOrderType;
  readonly isSmartWallet: boolean;
  readonly isWrapOrUnwrapPair: boolean;
  readonly includesNativeToken: boolean;
  readonly includesSuperstateToken: boolean;
  readonly sellTokenDecimals: number | undefined;
  readonly buyTokenDecimals: number | undefined;
  readonly sellAmountUnits: bigint | undefined;
  readonly sellTokenBalanceUnits: bigint | undefined;
  readonly allowanceUnits: bigint | undefined;
  readonly sellTokenRequiresApproval: boolean;
  readonly limitRate: number | undefined;
};

function appendAdvancedAmountAndBalanceResults(
  input: AdvancedOrderValidationInput,
  results: AdvancedValidationResults,
): void {
  if (input.sellAmountUnits === undefined) {
    results.push({ code: 'sell-amount-required', severity: 'blocking' });
  } else if (
    input.sellTokenBalanceUnits === undefined ||
    input.sellAmountUnits > input.sellTokenBalanceUnits
  ) {
    results.push({ code: 'insufficient-balance', severity: 'blocking' });
  }
}

function appendAdvancedApprovalResult(
  input: AdvancedOrderValidationInput,
  results: AdvancedValidationResults,
): void {
  if (
    input.sellAmountUnits !== undefined &&
    input.allowanceUnits !== undefined &&
    input.sellTokenRequiresApproval &&
    input.allowanceUnits < input.sellAmountUnits
  ) {
    results.push({
      code: input.allowanceUnits > 0n ? 'token-needs-approval-increase' : 'token-needs-approval',
      severity: 'blocking',
    });
  }
}

/** Validates an Advanced order and returns every applicable result in legacy-primary order. */
export function validateAdvancedOrder(
  input: AdvancedOrderValidationInput,
  overrides: AdvancedOrderProviderOverrides = ADVANCED_ORDER_PROVIDER_OVERRIDES,
): AdvancedValidationResults {
  const results: AdvancedValidationResults = [];
  const chainId = input.sellTokenChainId || input.buyTokenChainId;

  appendTokenRestrictionResults(input, results);

  if (chainId !== undefined && !isAdvancedOrderTypeSupported(chainId, input.orderType, overrides)) {
    results.push({
      code: 'advanced-order-type-unsupported',
      severity: 'blocking',
      chainId,
      orderType: input.orderType,
    });
  }
  if (
    input.isSmartWallet &&
    chainId !== undefined &&
    isLimitOrderSupportedChainId(chainId) &&
    getAdvancedOrderProvider(chainId, overrides) === '1inch'
  ) {
    results.push({ code: 'smart-wallet-unsupported', severity: 'blocking' });
  }
  if (
    input.sellTokenChainId !== undefined &&
    input.buyTokenChainId !== undefined &&
    input.sellTokenChainId !== input.buyTokenChainId
  ) {
    results.push({
      code: 'cross-chain-unsupported',
      severity: 'blocking',
      sellTokenChainId: input.sellTokenChainId,
      buyTokenChainId: input.buyTokenChainId,
    });
  }
  if (input.isWrapOrUnwrapPair) {
    results.push({ code: 'wrap-unwrap-unsupported', severity: 'blocking' });
  }
  if (input.includesNativeToken) {
    results.push({ code: 'native-token-unsupported', severity: 'blocking' });
  }
  if (input.includesSuperstateToken) {
    results.push({ code: 'superstate-token-unsupported', severity: 'blocking' });
  }
  if (input.sellTokenAddress !== undefined && input.sellTokenDecimals === undefined) {
    results.push({
      code: 'token-decimals-unavailable',
      severity: 'blocking',
      side: 'sell',
    });
  }
  if (input.buyTokenAddress !== undefined && input.buyTokenDecimals === undefined) {
    results.push({
      code: 'token-decimals-unavailable',
      severity: 'blocking',
      side: 'buy',
    });
  }

  if (input.checksApprovalBeforeBalance) {
    appendRequiredTokenResults(input, results);
    appendAdvancedApprovalResult(input, results);
    appendAdvancedAmountAndBalanceResults(input, results);
  } else {
    appendAdvancedAmountAndBalanceResults(input, results);
    appendRequiredTokenResults(input, results);
    appendAdvancedApprovalResult(input, results);
  }

  if (input.limitRate === undefined || input.limitRate === 0 || Number.isNaN(input.limitRate)) {
    results.push({ code: 'limit-rate-invalid', severity: 'blocking' });
  }

  appendHighPriceImpactResult(input, results);

  return results;
}
